import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const branchKeys = {
  all: ['branches'],
  lists: () => [...branchKeys.all, 'list'],
  details: () => [...branchKeys.all, 'detail'],
  detail: (id) => [...branchKeys.details(), id],
};

export const useBranches = () => {
  return useQuery({
    queryKey: branchKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('branches');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useBranch = (id, options = {}) => {
  return useQuery({
    queryKey: branchKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`branches/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreateBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (branchData) => {
      const response = await apiClient.post('branches', branchData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: branchKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating branch:', error);
    }
  });
};

export const useUpdateBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, branchData }) => {
      const response = await apiClient.put(`branches/${id}`, branchData);
      return response.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: branchKeys.lists() });
      queryClient.invalidateQueries({ queryKey: branchKeys.detail(id) });
    },
    onError: (error) => {
      console.error('Error updating branch:', error);
    }
  });
};

export const useDeleteBranch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`branches/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: branchKeys.lists() });
      queryClient.removeQueries({ queryKey: branchKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting branch:', error);
    }
  });
};